import { ToolDefinition } from "../types";

const pdfDpiCheckerTool: ToolDefinition = {
  slug: "pdf-dpi-checker",
  name: "PDF DPI Checker | Check PDF Resolution and PPI Online",
  title: "Check PDF DPI and Resolution Before Printing or Compressing",
  description:
    "Check the DPI, PPI, and embedded image resolution of any PDF online. Analyse per-page image quality before printing, publishing, or compressing.",
  longDescription:
    "Upload any PDF and instantly check the DPI and PPI of every embedded image across all pages. Get per-image results including pixel dimensions, DPI X, DPI Y, color space, and an average image DPI — no Acrobat required.",
  toolType: {
    label: "PDF Analyser",
    icon: "magnifying-glass",
  },
  heroBadges: [
    "Free Online",
    "No Acrobat Needed",
    "Per-Page DPI Results",
    "Up to 20 MB",
  ],
  category: "PDF Tools",
  categorySlug: "pdf-tools",
  href: "/tools/pdf-dpi-checker",
  api: "/api/v1/pdf/dpi-checker",
  keywords: [
    "snappy pdf dpi checker",
    "check pdf resolution online",
    "how to check pdf ppi",
    "verify pdf print quality online",
    "pdf image resolution analyzer",
    "convert low dpi pdf to 300",
    "change standard office pdf resolution",
    "high quality print pdf maker",
    "free online pdf validation app",
    "prepress document diagnostic tool",
    "find embedded photo pixels in pdf",
    "is my scanned booklet 300 dpi",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Check PDF DPI Online",
        description:
          "Analyse every embedded image in your PDF in three steps — no software installation and no Acrobat licence required.",
      },
      benefits: {
        title: "Why Use a Dedicated PDF DPI Checker?",
        description:
          "Get accurate per-page resolution data before your PDF goes to a print shop, publisher, or compression pipeline.",
      },
      faqs: {
        title: "PDF DPI Checker — Frequently Asked Questions",
        description:
          "Answers to the most common questions about checking PDF image resolution and PPI online.",
      },
    },
    intro: {
      title: "What Is PDF DPI and Why Does It Matter?",
      paragraphs: [
        "DPI (dots per inch) and PPI (pixels per inch) describe how many image pixels are packed into each inch of a printed or displayed page. When a PDF contains embedded images at too low a resolution — typically below 150 DPI — those images will appear blurry or pixelated when printed, even if the file looks sharp on screen.",
        "Professional print vendors usually require at least 300 DPI for sharp offset or digital print. Prepress teams reject files that fail this threshold, causing costly delays. Checking your PDF's embedded image DPI before submission lets you catch low-resolution pages early and decide whether to reprocess the source file.",
        "This tool reads the raw PDF image objects and calculates effective DPI from pixel dimensions and placement data — the same information a preflight tool would surface, available free online without installing anything.",
      ],
      note: "This tool checks embedded image DPI only. It does not change, repair, compress, or extract images from your PDF.",
    },
    introBlocks: [
      {
        kind: "table",
        tables: [
          {
            title: "PDF Image DPI Thresholds at a Glance",
            description:
              "Common DPI benchmarks used by print shops, publishers, and web workflows.",
            columns: ["DPI Range", "Typical Use Case", "Print-Ready?"],
            rows: [
              {
                "DPI Range": "72 – 96 DPI",
                "Typical Use Case": "Screen display, web, email",
                "Print-Ready?": "No",
              },
              {
                "DPI Range": "150 – 199 DPI",
                "Typical Use Case": "Low-quality draft prints",
                "Print-Ready?": "Marginal",
              },
              {
                "DPI Range": "200 – 299 DPI",
                "Typical Use Case": "Newspapers, internal docs",
                "Print-Ready?": "Sometimes",
              },
              {
                "DPI Range": "300 DPI",
                "Typical Use Case": "Standard commercial print",
                "Print-Ready?": "Yes",
              },
              {
                "DPI Range": "400 – 600 DPI",
                "Typical Use Case": "Fine art, photo books, scanning",
                "Print-Ready?": "Yes — archival",
              },
              {
                "DPI Range": "1200+ DPI",
                "Typical Use Case": "Line art, vector-rasterised output",
                "Print-Ready?": "Yes — specialist",
              },
            ],
            note: "300 DPI is the widely accepted minimum for professional offset and digital print. Always confirm requirements with your print vendor.",
            emphasisColumns: ["Print-Ready?"],
            monospaceColumns: ["DPI Range"],
          },
        ],
      },
      {
        kind: "text",
        title: "What the DPI Checker Actually Analyses",
        level: "h2",
        paragraphs: [
          "When you upload a PDF, the analyser iterates through every embedded image object across all pages. For each image it calculates DPI X and DPI Y from the stored pixel dimensions and the rendered size on the page. It also reads color space (RGB, CMYK, Grayscale) and compression method where available.",
          "The results include: page number, image index, pixel width, pixel height, DPI X, DPI Y, color space, compression, and an average image DPI across the whole document. This gives you a complete picture of where low-resolution assets are hiding — even on pages that look fine at 100% zoom.",
        ],
        note: "A PDF can have different DPI values on different pages. The per-page breakdown lets you identify exactly which pages need attention.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Common Reasons to Check PDF Resolution",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Print shop rejected your file",
                icon: "printer",
                description:
                  "Prepress teams flag PDFs with images below 300 DPI. Check before you submit to avoid reprint fees and delays.",
                accent: "primary",
              },
              {
                label: "Scanned document quality audit",
                icon: "scan",
                description:
                  "Confirm whether a scanned PDF was captured at 300, 400, or 600 DPI before archiving or distributing it.",
                accent: "primary",
              },
              {
                label: "PDF looks sharp on screen but prints blurry",
                icon: "eye",
                description:
                  "Screen rendering upscales low-resolution images. Actual DPI values reveal the true print quality hidden by zoom.",
                accent: "primary",
              },
              {
                label: "Pre-compression quality baseline",
                icon: "compress",
                description:
                  "Record current image DPI before running compression so you can verify that critical pages weren't degraded.",
                accent: "primary",
              },
              {
                label: "Publisher or magazine submission check",
                icon: "book-open",
                description:
                  "Editorial and photo-book publishers specify minimum PPI. Verify every page meets the brief before sending.",
                accent: "primary",
              },
            ],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Perfect for Print Workflows",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "Check Before Printing",
            icon: "printer",
            description:
              "Avoid costly reprint mistakes by verifying your document is at least 300 DPI before sending it to the presses.",
            accent: "primary",
          },
          {
            label: "Audit Scanned Documents",
            icon: "scanner",
            description:
              "Ensure your digital archives and scanned paperwork meet the required resolution for compliance and legibility.",
            accent: "primary",
          },
        ],
      },
    ],
    referenceTables: [
      {
        title: "API Response Fields Explained",
        description:
          "Fields returned by the PDF DPI Checker endpoint for each analysed image.",
        columns: ["Field", "Type", "Description"],
        rows: [
          {
            Field: "page_count",
            Type: "integer",
            Description: "Total number of pages in the uploaded PDF",
          },
          {
            Field: "images[].page",
            Type: "integer",
            Description: "Page number where the image appears (1-indexed)",
          },
          {
            Field: "images[].index",
            Type: "integer",
            Description: "Image index on that page",
          },
          {
            Field: "images[].width_px",
            Type: "integer",
            Description: "Image width in pixels",
          },
          {
            Field: "images[].height_px",
            Type: "integer",
            Description: "Image height in pixels",
          },
          {
            Field: "images[].dpi_x",
            Type: "number",
            Description: "Horizontal resolution in dots per inch",
          },
          {
            Field: "images[].dpi_y",
            Type: "number",
            Description: "Vertical resolution in dots per inch",
          },
          {
            Field: "images[].color_space",
            Type: "string",
            Description: "Color space (e.g. RGB, CMYK, Grayscale)",
          },
          {
            Field: "images[].compression",
            Type: "string",
            Description: "Compression type when available (e.g. JPEG, CCITT)",
          },
          {
            Field: "average_image_dpi",
            Type: "number",
            Description: "Mean DPI across all embedded images in the document",
          },
          {
            Field: "processing_time_ms",
            Type: "integer",
            Description: "Server-side processing time in milliseconds",
          },
        ],
        note: "The compression and color_space fields are returned when the PDF metadata includes them. Not all PDFs expose these values.",
        emphasisColumns: ["Field"],
        monospaceColumns: ["Field", "Type"],
      },
    ],
    faqs: [
      {
        question: "How do I check the DPI of images embedded in a PDF?",
        answer:
          "Upload your PDF to this tool. It reads each embedded image object and calculates DPI X and DPI Y from the pixel dimensions and the rendered placement on the page. Results are shown per image, per page, with an average DPI for the whole document.",
      },
      {
        question:
          "How can I tell if my PDF is 300 DPI for professional printing?",
        answer:
          "Run the PDF through this checker and look at the DPI X and DPI Y columns for each image. If all images report 300 DPI or higher, the document meets the standard commercial print threshold. Any image below 300 DPI on a print-critical page should be replaced with a higher-resolution source.",
      },
      {
        question: "Can a PDF have different DPI values on different pages?",
        answer:
          "Yes. A PDF is a container — each embedded image stores its own resolution data independently. A cover page might hold a 72 DPI screen-exported photo while interior pages contain 300 DPI scans. This tool shows per-page, per-image DPI so you can identify exactly which pages have resolution problems.",
      },
      {
        question: "Why does my PDF look sharp on screen but print blurry?",
        answer:
          "Screen rendering interpolates and upscales images to fill display pixels, masking low resolution. A 96 DPI image can look clean at 100% zoom on a monitor but will print at noticeably reduced quality. Checking the actual embedded DPI reveals the real print quality rather than the screen approximation.",
      },
      {
        question:
          "How can I check a PDF's PPI online for free without Acrobat?",
        answer:
          "This tool does exactly that. Upload your PDF (up to 20 MB, up to 5000 pages) and it returns per-image PPI and DPI data directly in your browser — no Adobe Acrobat licence or desktop software required.",
      },
      {
        question: "Does this tool change or fix the DPI of my PDF?",
        answer:
          "No. This is a read-only analyser. It inspects and reports embedded image resolution data but does not modify, compress, or reprocess your PDF in any way. If you need to change DPI, use a dedicated PDF DPI changer after reviewing the results here.",
      },
      {
        question:
          "What is the maximum file size and page count this tool supports?",
        answer:
          "The PDF DPI Checker accepts files up to 20 MB and supports documents up to 5000 pages. Files larger than 20 MB should be split before uploading.",
      },
      {
        question:
          "What PDF image information does the tool return besides DPI?",
        answer:
          "Each image result includes page number, image index, pixel width, pixel height, DPI X, DPI Y, color space (RGB, CMYK, or Grayscale), and compression method when available. A document-level average image DPI and processing time are also returned.",
      },
    ],
    benefits: [
      {
        icon: "search",
        title: "Per-Page DPI Breakdown",
        description:
          "See DPI X, DPI Y, pixel dimensions, color space, and compression for every embedded image — not just a single document-wide average.",
      },
      {
        icon: "printer",
        title: "Catch Low-Resolution Pages Before Print",
        description:
          "Identify pages where embedded images fall below 300 DPI before your file reaches the print shop, avoiding costly reprints and submission rejections.",
      },
      {
        icon: "shield-check",
        title: "No Acrobat or Software Required",
        description:
          "Check PDF resolution directly in your browser. No Adobe licence, no desktop install, and no plugin needed.",
      },
      {
        icon: "layers",
        title: "Supports Large Multi-Page PDFs",
        description:
          "Analyse documents up to 20 MB and 5000 pages. Suitable for brochures, catalogues, reports, and scanned archive files.",
      },
      {
        icon: "palette",
        title: "Color Space Visibility",
        description:
          "Each image result includes its color space — RGB, CMYK, or Grayscale — helping prepress teams verify color mode alongside resolution.",
      },
      {
        icon: "bar-chart",
        title: "Average Image DPI Summary",
        description:
          "Get a document-wide average image DPI alongside per-image data, giving you a fast headline figure for quick quality assessments.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload Your PDF",
        description:
          "Select a PDF file up to 20 MB. The tool validates the upload and supports documents with up to 5000 pages.",
      },
      {
        number: "02",
        title: "Analyse Embedded Images",
        description:
          "The checker reads every image object inside the PDF and calculates effective DPI and PPI from pixel dimensions and page placement data.",
      },
      {
        number: "03",
        title: "Review Per-Page Resolution Results",
        description:
          "Inspect page number, image index, width, height, DPI X, DPI Y, color space, compression, and average image DPI for the full document.",
      },
      {
        number: "04",
        title: "Plan Your Next Step",
        description:
          "Use the findings to confirm print readiness, flag low-resolution pages for reprocessing, or pass the data into a PDF DPI changer workflow.",
      },
    ],
    useCases: [
      {
        label: "Print Preflight",
        icon: "printer",
        description:
          "Verify that all images in a PDF meet the 300 DPI threshold before sending to a commercial or fine-art print vendor.",
        accent: "primary",
      },
      {
        label: "Scanned Document Audit",
        icon: "scan",
        description:
          "Confirm scan resolution on archival or compliance documents before long-term storage or distribution.",
        accent: "primary",
      },
      {
        label: "Pre-Compression Baseline",
        icon: "compress",
        description:
          "Record current image DPI values before running PDF compression so you can detect any resolution loss introduced by the process.",
        accent: "primary",
      },
      {
        label: "Publisher Submission Check",
        icon: "book-open",
        description:
          "Ensure every page of a photo book, magazine layout, or editorial PDF meets the publisher's minimum PPI specification.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Graphic Designers",
        icon: "pen-tool",
        description:
          "Verify exported PDFs meet print-ready DPI standards before sending files to a print bureau or client.",
        accent: "accent",
      },
      {
        label: "Prepress Technicians",
        icon: "settings",
        description:
          "Run a fast resolution check on incoming PDFs to flag low-quality image pages before they reach the press.",
        accent: "accent",
      },
      {
        label: "Document Archivists",
        icon: "archive",
        description:
          "Audit scanned PDFs to confirm they were captured at the required 300–600 DPI before adding them to an archive.",
        accent: "accent",
      },
      {
        label: "Developers and DevOps",
        icon: "code",
        description:
          "Integrate PDF DPI checking into automated pipelines to validate document quality before storage, distribution, or conversion.",
        accent: "accent",
      },
    ],
  },
};

// {
//   slug: "pdf-dpi-checker",
//   name: "PDF DPI Checker | Check PDF Resolution and PPI Online",
//   title: "PDF DPI Checker",
//   description:
//     "Check the DPI and resolution of embedded images in your PDF files online. Analyze PPI per page to ensure your document is ready for high-quality printing.",
//   longDescription:
//     "Instantly check the DPI and resolution of embedded images inside any PDF document. Our free online PDF DPI checker analyzes your file to extract pixel dimensions, color space, and average PPI. Verify if your PDF meets the 300 DPI standard for professional printing, prepress, or high-quality publishing without downloading heavy software.",
//   toolType: {
//     label: "PDF Analyzer",
//     icon: "magnifying-glass",
//   },
//   heroBadges: [
//     "Free Online Checker",
//     "No Installation Required",
//     "Prepress & Print Ready",
//   ],
//   category: "PDF Tools",
//   categorySlug: "pdf-tools",
//   href: "/tools/pdf-dpi-checker",
//   api: "/api/v1/pdf/dpi-checker",
//   keywords: [
//     "snappy pdf dpi checker",
//     "pdf resolution checker",
//     "pdf ppi checker",
//     "check pdf dpi online",
//     "pdf image resolution checker",
//     "pdf print quality checker",
//     "find dpi of pdf",
//     "pdf dpi analyzer",
//     "pdf scan resolution checker",
//     "is my pdf 300 dpi",
//   ],
//   pageContent: {
//     sections: {
//       steps: {
//         title: "How to Check PDF DPI Online",
//         description:
//           "Follow these simple steps to analyze the embedded image resolution and PPI of your PDF document.",
//       },
//       benefits: {
//         title: "Why Use Our PDF Resolution Checker?",
//         description:
//           "Ensure your documents meet strict professional standards with our detailed, fast, and secure PDF image quality analyzer.",
//       },
//       faqs: {
//         title: "Frequently Asked Questions",
//         description:
//           "Learn more about PDF print quality, the difference between DPI and PPI, and how to verify if your document is ready for professional printing.",
//       },
//     },
//     intro: {
//       title: "Verify Your PDF Print Quality Instantly",
//       paragraphs: [
//         "Checking the DPI (Dots Per Inch) or PPI (Pixels Per Inch) of a PDF is a critical step before sending any document to a commercial printer or submitting it for digital publication. Often, a PDF will look perfectly sharp and clear on your computer screen but print out blurry, pixelated, or heavily artifacted. This happens because screen resolution typically only requires 72 to 96 PPI to look good, while professional print workflows demand a minimum of 300 DPI for crisp, high-quality results.",
//         "Our PDF DPI Checker completely automates the process of analyzing your document's internal resolution. Instead of guessing the quality or relying on expensive desktop publishing software to inspect the file, you can simply upload your PDF directly to your browser. The tool safely scans every page, locates all embedded image objects, calculates their effective dimensions, and reports the exact DPI X and DPI Y values.",
//         "Whether you need to find low-resolution images in a scanned document, confirm an average PDF image DPI for a printing press, or simply understand why a specific page looks fuzzy, this tool provides the exact diagnostic data you need to move forward confidently.",
//       ],
//       note: "This tool strictly analyzes the PDF to check image quality. It does not alter, compress, or repair low-resolution images.",
//     },
//     introBlocks: [
//       {
//         kind: "text",
//         title: "Understanding PDF Resolution and Embedded Images",
//         level: "h2",
//         paragraphs: [
//           "A common misconception is that a PDF file itself has a single, overarching DPI. In reality, a PDF is a container. It holds text, vector graphics, and embedded raster images. Text and vectors are mathematically drawn and will scale infinitely without losing quality—they are technically 'resolution-independent.' Raster images (like photographs or scanned pages), however, have a fixed number of pixels. The DPI or PPI of a PDF is entirely dependent on the resolution of these embedded raster images and the physical size at which they are placed on the document page.",
//           "Because a PDF can contain dozens of images at varying sizes, a single document can have different DPI values on different pages, or even multiple different resolutions on the exact same page. Our PDF print quality checker goes deep into the file structure. It calculates the effective resolution by taking the pixel width and height of the raw image object and dividing it by the physical dimensions (in inches) it occupies on the PDF page.",
//         ],
//         note: "Vectors and text do not have a DPI. Only embedded photographs, rasterized graphics, and scanned document pages will return a PPI/DPI value.",
//       },
//       {
//         kind: "table",
//         tables: [
//           {
//             title: "Standard Resolution Thresholds for PDFs",
//             description:
//               "Use this guide to determine if the DPI reported by our analyzer meets your specific output requirements.",
//             columns: [
//               "Resolution (DPI/PPI)",
//               "Common Use Case",
//               "Expected Quality Output",
//             ],
//             rows: [
//               {
//                 "Resolution (DPI/PPI)": "72 - 96 DPI",
//                 "Common Use Case": "Web viewing, email attachments",
//                 "Expected Quality Output":
//                   "Sharp on screens, very blurry and pixelated when printed.",
//               },
//               {
//                 "Resolution (DPI/PPI)": "150 - 200 DPI",
//                 "Common Use Case": "Office printers, draft documents",
//                 "Expected Quality Output":
//                   "Acceptable for reading text, but images will lack fine detail.",
//               },
//               {
//                 "Resolution (DPI/PPI)": "300 DPI",
//                 "Common Use Case": "Professional printing, magazines",
//                 "Expected Quality Output":
//                   "Industry standard. High quality, crisp lines, and clear photos.",
//               },
//               {
//                 "Resolution (DPI/PPI)": "600+ DPI",
//                 "Common Use Case": "Fine art prints, archival scanning",
//                 "Expected Quality Output":
//                   "Maximum detail for specialized, large-format, or gallery printing.",
//               },
//             ],
//             note: "If your average PDF image DPI falls below 300, consider replacing the original images before sending the file to a print shop.",
//             emphasisColumns: ["Resolution (DPI/PPI)"],
//             monospaceColumns: [],
//           },
//         ],
//       },
//       {
//         kind: "callouts",
//         groups: [
//           {
//             title: "What the PDF DPI Analyzer Reports",
//             layout: "list",
//             iconColor: "primary",
//             items: [
//               {
//                 label: "Per-Page Dimensions",
//                 icon: "ruler",
//                 description:
//                   "Identifies the exact pixel width and height of every embedded image found on a specific page.",
//                 accent: "primary",
//               },
//               {
//                 label: "Effective DPI X and DPI Y",
//                 icon: "target",
//                 description:
//                   "Calculates horizontal (X) and vertical (Y) resolution based on how the image is scaled on the page.",
//                 accent: "primary",
//               },
//               {
//                 label: "Average Image DPI",
//                 icon: "calculator",
//                 description:
//                   "Provides a quick summary metric of the overall document quality to help you assess print readiness at a glance.",
//                 accent: "primary",
//               },
//               {
//                 label: "Color Space & Compression",
//                 icon: "palette",
//                 description:
//                   "Reveals whether the image is RGB, CMYK, or Grayscale, and what compression method (e.g., JPEG, Flate) is used.",
//                 accent: "primary",
//               },
//             ],
//           },
//         ],
//       },
//     ],
//     callouts: [
//       {
//         title: "Perfect for Print Workflows",
//         layout: "list",
//         iconColor: "primary",
//         items: [
//           {
//             label: "Check Before Printing",
//             icon: "printer",
//             description:
//               "Avoid costly reprint mistakes by verifying your document is at least 300 DPI before sending it to the presses.",
//             accent: "primary",
//           },
//           {
//             label: "Audit Scanned Documents",
//             icon: "scanner",
//             description:
//               "Ensure your digital archives and scanned paperwork meet the required resolution for compliance and legibility.",
//             accent: "primary",
//           },
//         ],
//       },
//     ],
//     referenceTables: [
//       {
//         title: "API Output Field Reference",
//         description:
//           "If you are reviewing the raw JSON data generated by the DPI checker, here is what each field represents.",
//         columns: ["Data Field", "Description"],
//         rows: [
//           {
//             "Data Field": "page_count",
//             Description:
//               "The total number of pages successfully analyzed in the uploaded PDF.",
//           },
//           {
//             "Data Field": "average_image_dpi",
//             Description:
//               "The mean DPI calculated across all embedded raster images found in the document.",
//           },
//           {
//             "Data Field": "page_number",
//             Description:
//               "The specific page (1-indexed) where the image object is located.",
//           },
//           {
//             "Data Field": "dpi_x / dpi_y",
//             Description:
//               "The effective horizontal and vertical resolution of the image as placed on the page.",
//           },
//           {
//             "Data Field": "color_space",
//             Description:
//               "The color model of the embedded image, such as DeviceRGB, DeviceCMYK, or DeviceGray.",
//           },
//         ],
//         note: "The API endpoint processes up to 5000 pages per document.",
//         emphasisColumns: ["Data Field"],
//         monospaceColumns: ["Data Field"],
//       },
//     ],
//     faqs: [
//       {
//         question: "How do I know if a PDF is high resolution for printing?",
//         answer:
//           "To ensure a PDF is high resolution for printing, you need to check the DPI (Dots Per Inch) of its embedded images. Professional printing requires a minimum of 300 DPI. You can upload your file to our PDF DPI Checker, which will scan the document and provide an average image DPI as well as a per-page breakdown. If the results show 300 DPI or higher, your PDF is print-ready.",
//       },
//       {
//         question: "What is the difference between PDF DPI and PPI?",
//         answer:
//           "PPI (Pixels Per Inch) refers to the digital resolution of an image on a screen, dictating how many pixels are packed into one inch. DPI (Dots Per Inch) is a printing term referring to the physical ink dots a printer places on paper. In digital document preparation, these terms are often used interchangeably. When our tool analyzes a PDF, it calculates the effective PPI of the digital images, which directly translates to the expected DPI when printed.",
//       },
//       {
//         question: "Can a PDF have different DPI values on different pages?",
//         answer:
//           "Yes, absolutely. A PDF does not have one global resolution. It is a container that holds various elements. If Page 1 contains a high-quality photograph saved at 300 DPI, and Page 2 contains a low-quality web logo saved at 72 DPI, the tool will report those different values for each specific image and page.",
//       },
//       {
//         question: "Why does a PDF look sharp on screen but print blurry?",
//         answer:
//           "Computer monitors generally display graphics at 72 to 96 PPI. An image saved at 72 PPI will look perfectly crisp on your screen because it matches the monitor's output capability. However, commercial printers need much more data—usually 300 DPI—to create a sharp physical print. If you print a 72 PPI image, the printer has to stretch the available pixels, resulting in a blurry, pixelated look.",
//       },
//       {
//         question: "Is my PDF 300 DPI?",
//         answer:
//           "You can find out exactly if your PDF is 300 DPI by uploading it to our checker. The tool will parse the embedded objects and give you a precise readout. If the 'average image dpi' or the specific per-page DPI X/Y values hit 300 or above, your document meets the standard.",
//       },
//       {
//         question: "Does this tool change the DPI of my PDF?",
//         answer:
//           "No, this is strictly a diagnostic analysis tool. It reads the PDF image objects and calculates their resolution data. It does not repair low-resolution images, upscale the file, or compress the document.",
//       },
//     ],
//     benefits: [
//       {
//         icon: "check-circle",
//         title: "Accurate Prepress Validation",
//         description:
//           "Quickly verify that your document meets the strict 300 DPI requirements of commercial print shops before you commit to a costly print run.",
//       },
//       {
//         icon: "magnifying-glass-plus",
//         title: "Deep Image Analysis",
//         description:
//           "Don't just guess based on file size. Extract exact pixel dimensions, DPI X/Y axes, color space, and compression types for every image inside the PDF.",
//       },
//       {
//         icon: "bolt",
//         title: "Fast and Browser-Based",
//         description:
//           "Check PDF resolution online in seconds. There is no need to install heavy desktop publishing software or navigate complex preflight menus.",
//       },
//       {
//         icon: "shield-check",
//         title: "Secure Processing",
//         description:
//           "Your files are securely processed for analysis and are never stored or shared, ensuring your sensitive documents remain private.",
//       },
//     ],
//     steps: [
//       {
//         number: "01",
//         title: "Upload Your PDF",
//         description:
//           "Select and upload the PDF file you want to inspect. The tool supports documents up to 20 MB and up to 5000 pages.",
//       },
//       {
//         number: "02",
//         title: "Run DPI Analysis",
//         description:
//           "Once uploaded, the tool automatically inspects the internal structure of the PDF, locating all embedded raster images and calculating their effective DPI/PPI values based on physical placement.",
//       },
//       {
//         number: "03",
//         title: "Review Image Results",
//         description:
//           "Inspect the generated report. Review the overall average image DPI, or dig into the per-page metrics including image width, height, DPI X, DPI Y, color space, and compression type.",
//       },
//       {
//         number: "04",
//         title: "Plan Your Workflow",
//         description:
//           "Use the findings to make informed decisions. Confirm the file is print-ready, diagnose pages with low-resolution graphics, or route the document to an editing tool if corrections are necessary.",
//       },
//     ],
//     useCases: [
//       {
//         label: "Print Preparation",
//         icon: "printer",
//         description:
//           "Confirm brochures, posters, and books meet 300 DPI requirements.",
//         accent: "primary",
//       },
//       {
//         label: "Prepress Troubleshooting",
//         icon: "wrench",
//         description:
//           "Locate specifically which page is causing a low-resolution warning at the print shop.",
//         accent: "primary",
//       },
//       {
//         label: "Archival Quality Check",
//         icon: "archive",
//         description:
//           "Ensure scanned documents meet the 600 DPI threshold for digital archiving.",
//         accent: "primary",
//       },
//     ],
//     audiences: [
//       {
//         label: "Graphic Designers",
//         icon: "pen-tool",
//         description:
//           "Need to ensure client proofs are perfectly optimized for final print production.",
//         accent: "accent",
//       },
//       {
//         label: "Prepress Technicians",
//         icon: "cog",
//         description:
//           "Require fast diagnostics to accept or reject incoming manuscript submissions.",
//         accent: "accent",
//       },
//       {
//         label: "Office Administrators",
//         icon: "briefcase",
//         description:
//           "Want to double-check that scanned contracts are high enough quality to remain legible.",
//         accent: "accent",
//       },
//     ],
//   },
// };

export default pdfDpiCheckerTool;
export { pdfDpiCheckerTool };
